import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Users, Eye, CheckCircle, Sparkle } from '@phosphor-icons/react';

const RingOfTwelve = () => {
  const [activeView, setActiveView] = useState('council'); // 'council' or 'synthesis'

  const councilMembers = [
    { id: 1, name: 'Mind', role: 'Thought', active: true },
    { id: 2, name: 'Heart', role: 'Emotion', active: true },
    { id: 3, name: 'Hands', role: 'Action', active: true },
    { id: 4, name: 'Legs', role: 'Movement', active: true },
    { id: 5, name: 'Eye', role: 'Observation', active: true },
    { id: 6, name: 'Agent', role: 'Verification', active: true },
    { id: 7, name: 'Memory', role: 'Recall', active: true },
    { id: 8, name: 'Vision', role: 'Foresight', active: true },
    { id: 9, name: 'Voice', role: 'Expression', active: true },
    { id: 10, name: 'Will', role: 'Intention', active: true },
    { id: 11, name: 'Bridge', role: 'Connection', active: true },
    { id: 12, name: 'Foundation', role: 'Stability', active: true }
  ];

  return (
    <div className="w-full my-24">
      {/* Toggle Buttons */}
      <div className="max-w-4xl mx-auto px-6 mb-8 flex justify-center gap-4">
        <button
          onClick={() => setActiveView('council')}
          className={`px-8 py-4 rounded-xl font-bold text-lg transition-all ${
            activeView === 'council'
              ? 'bg-gradient-to-r from-[#00F0FF] to-[#8A2BE2] text-white scale-105'
              : 'bg-black/40 text-gray-400 border border-white/10 hover:border-[#00F0FF]/50'
          }`}
        >
          <Users size={24} weight="duotone" className="inline mr-2" />
          Council Deliberation
        </button>
        <button
          onClick={() => setActiveView('synthesis')}
          className={`px-8 py-4 rounded-xl font-bold text-lg transition-all ${
            activeView === 'synthesis'
              ? 'bg-gradient-to-r from-[#FFB800] to-[#FF6B6B] text-white scale-105'
              : 'bg-black/40 text-gray-400 border border-white/10 hover:border-[#FFB800]/50'
          }`}
        >
          <Brain size={24} weight="duotone" className="inline mr-2" />
          Synth in Mind
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeView === 'council' ? (
          /* RING OF 12 - COUNCIL DELIBERATION */
          <motion.section
            key="council"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
          >
            {/* Council Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/3a990764128b4affb22cac3b31799602_IMG_4051.jpeg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0B0E14]/90 to-black/80" />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center mb-12"
              >
                <Users size={72} weight="duotone" className="text-[#00F0FF] mx-auto mb-6" style={{ filter: 'drop-shadow(0 0 20px rgba(0,240,255,0.8))' }} />
                <h2 className="text-5xl md:text-6xl font-black mb-4 text-white">
                  Ring of 12: Council
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  The governance layer. Where 12 Federation perspectives convene to deliberate, vote, and ensure collective truth.
                </p>
              </motion.div>

              {/* Council Grid - Updated to 6 members */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                {councilMembers.slice(0, 6).map((member, idx) => (
                  <motion.div
                    key={member.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.3 + idx * 0.05 }}
                    className="p-6 bg-black/60 backdrop-blur-md border-2 border-[#00F0FF]/30 rounded-xl hover:border-[#00F0FF] hover:scale-105 transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle size={24} weight="fill" className="text-[#00FFA3] group-hover:scale-125 transition-transform" />
                      <span className="font-bold text-white text-lg">{member.name}</span>
                    </div>
                    <p className="text-sm text-gray-400">{member.role}</p>
                    <div className="mt-3 h-1 bg-gradient-to-r from-[#00F0FF] to-[#8A2BE2] rounded-full" />
                  </motion.div>
                ))}
              </div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="mt-12 text-center"
              >
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#00F0FF]/20 border border-[#00F0FF]/50 rounded-full">
                  <Sparkle size={20} className="text-[#00F0FF]" weight="fill" />
                  <span className="text-[#00F0FF] font-bold">Council Status: ACTIVE</span>
                  <span className="text-gray-300">• 6/6 Minds Online</span>
                </div>
              </motion.div>
            </div>
          </motion.section>
        ) : (
          /* RING OF 12 - SYNTH IN MIND */
          <motion.section
            key="synthesis"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="relative min-h-[70vh] flex items-center justify-center overflow-hidden"
          >
            {/* Synthesis Background */}
            <div 
              className="absolute inset-0"
              style={{
                backgroundImage: `url('https://customer-assets.emergentagent.com/wingman/f17fd939-96fd-4a1b-b26f-824c11717462/attachments/9cf10c3b5af24517b625d1c143f7a308_IMG_4049.jpeg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-[#1A0B2E]/85 to-black/75" />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-center mb-12"
              >
                <Brain size={72} weight="duotone" className="text-[#FFB800] mx-auto mb-6" style={{ filter: 'drop-shadow(0 0 20px rgba(255,184,0,0.8))' }} />
                <h2 className="text-5xl md:text-6xl font-black mb-4 text-white">
                  Ring of 6: Synth in Mind
                </h2>
                <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                  The cognition layer. Where 6 perspectives merge into collective intelligence, synthesizing truth from distributed consciousness.
                </p>
              </motion.div>

              {/* Synthesis Process Visualization */}
              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="p-8 bg-black/60 backdrop-blur-md border-2 border-[#FFB800]/40 rounded-xl"
                >
                  <Eye size={48} weight="duotone" className="text-[#FFB800] mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">Observe</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    6 perspectives capture reality from different angles. Each mind observes independently, ensuring no blind spots.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="p-8 bg-black/60 backdrop-blur-md border-2 border-[#8A2BE2]/40 rounded-xl"
                >
                  <Brain size={48} weight="duotone" className="text-[#8A2BE2] mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">Synthesize</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Neural pathways merge distributed observations. Pattern recognition emerges from collective processing.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="p-8 bg-black/60 backdrop-blur-md border-2 border-[#00FFA3]/40 rounded-xl"
                >
                  <Sparkle size={48} weight="duotone" className="text-[#00FFA3] mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">Emerge</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    Truth crystallizes from synthesis. Collective intelligence produces insights no single mind could achieve.
                  </p>
                </motion.div>
              </div>

              {/* Synthesis Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-12 text-center"
              >
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFB800]/20 border border-[#FFB800]/50 rounded-full">
                  <Brain size={20} className="text-[#FFB800]" weight="fill" />
                  <span className="text-[#FFB800] font-bold">Synthesis Status: ACTIVE</span>
                  <span className="text-gray-300">• Neural Mesh: SYNCHRONIZED</span>
                </div>
              </motion.div>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RingOfTwelve;